import { Button } from "@nexus/ui"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  countHandoffSectionEdits,
  evaluateVendorHandoffChecks,
  type MarketplaceOrderHandoffDefaults,
  type TargetRatio,
} from "@nexus/contracts"

import { previewOrderHandoffEmail } from "@/features/marketplace/api/marketplace.api"
import {
  VendorEmailFrame,
  parseAddressList,
  type VendorEmailViewMode,
} from "@/features/marketplace/components/vendor-email/VendorEmailFrame"
import {
  readVendorEmailDraft,
  vendorEmailDraftKey,
  writeVendorEmailDraft,
} from "@/features/marketplace/lib/vendor-email-draft"
import { orderHandoffEmailPreviewQueryOptions } from "@/lib/queries"
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard"

interface SendOrderReviewStepProps {
  tenantId: string
  tenantVendorId: string
  scriptIds: string[]
  briefIds?: string[]
  vendorName: string
  handoffOverrideNote: string
  handoffDefaults: MarketplaceOrderHandoffDefaults
  targetRatios?: readonly TargetRatio[]
  baseline: MarketplaceOrderHandoffDefaults
  recipientsTo: string
  recipientsCc: string
  subject: string
  onRecipientsToChange: (next: string) => void
  onRecipientsCcChange: (next: string) => void
  onSubjectChange: (next: string) => void
  onCanSendChange?: (canSend: boolean) => void
  onEditSource?: () => void
  disabled?: boolean
}

export function parseReviewRecipients(value: string): string[] {
  return parseAddressList(value)
}

export function SendOrderReviewStep({
  tenantId,
  tenantVendorId,
  scriptIds,
  briefIds = [],
  vendorName,
  handoffOverrideNote,
  handoffDefaults,
  targetRatios = [],
  baseline,
  recipientsTo,
  recipientsCc,
  subject,
  onRecipientsToChange,
  onRecipientsCcChange,
  onSubjectChange,
  onCanSendChange,
  onEditSource,
  disabled = false,
}: SendOrderReviewStepProps) {
  const { t } = useTranslation()
  const [view, setView] = useState<VendorEmailViewMode>("desktop")
  const [bcc, setBcc] = useState("")
  const [restored, setRestored] = useState(false)
  const { copy } = useCopyToClipboard({
    onCopy: () => toast.success(t("marketplace.orders.list.brief.copied", "Copied")),
  })

  const draftKey = vendorEmailDraftKey(tenantId, tenantVendorId, scriptIds, briefIds)

  useEffect(() => {
    if (restored) return
    const draft = readVendorEmailDraft(draftKey)
    if (draft) {
      onRecipientsToChange(draft.recipientsTo)
      onRecipientsCcChange(draft.recipientsCc)
      if (draft.subject) onSubjectChange(draft.subject)
      setBcc(draft.recipientsBcc)
    }
    setRestored(true)
  }, [draftKey, onRecipientsCcChange, onRecipientsToChange, onSubjectChange, restored])

  useEffect(() => {
    if (!restored) return
    writeVendorEmailDraft(draftKey, {
      recipientsTo,
      recipientsCc,
      recipientsBcc: bcc,
      subject,
      handoffOverrideNote,
      handoffDefaults,
      updatedAt: new Date().toISOString(),
    })
  }, [bcc, draftKey, handoffDefaults, handoffOverrideNote, recipientsCc, recipientsTo, restored, subject])

  const previewPayload = useMemo(
    () => ({
      tenantId,
      tenantVendorId,
      scriptIds,
      briefIds,
      handoffOverrideNote,
      handoffDefaultsOverride: handoffDefaults,
      ...(targetRatios.length > 0 ? { targetRatios: [...targetRatios] } : {}),
      recipientEmails: parseReviewRecipients(recipientsTo),
      ccEmails: parseReviewRecipients(recipientsCc),
      bccEmails: parseReviewRecipients(bcc),
      subject: subject.trim() || undefined,
    }),
    [bcc, briefIds, handoffDefaults, handoffOverrideNote, recipientsCc, recipientsTo, scriptIds, subject, targetRatios, tenantId, tenantVendorId],
  )
  const to = previewPayload.recipientEmails
  const cc = previewPayload.ccEmails

  const previewQuery = useQuery(orderHandoffEmailPreviewQueryOptions(previewPayload))

  const preview = previewQuery.data
  const resolvedSubject = subject.trim() || preview?.subject || ""
  const localChecks = evaluateVendorHandoffChecks({
    to,
    cc,
    bcc: parseReviewRecipients(bcc),
    subject: resolvedSubject,
    orderInstructions: handoffOverrideNote,
    defaults: handoffDefaults,
  })
  const checks = preview?.checks ?? localChecks
  const canSend = checks.blockers.length === 0

  useEffect(() => {
    onCanSendChange?.(canSend)
  }, [canSend, onCanSendChange])

  useEffect(() => {
    if (!subject.trim() && preview?.subject) onSubjectChange(preview.subject)
  }, [onSubjectChange, preview?.subject, subject])

  const edits = countHandoffSectionEdits(handoffDefaults, baseline)
  const editedCount =
    edits.rules + edits.brandKit + (handoffOverrideNote.trim() ? 1 : 0)

  const testSend = useMemo(
    () => ({
      mutate: async () => {
        const result = await previewOrderHandoffEmail({ ...previewPayload, sendTest: true })
        toast.success(
          result.testSentTo
            ? t("marketplace.sendHandoff.testSentTo", {
                email: result.testSentTo,
                defaultValue: "Test email sent to {{email}}",
              })
            : t("marketplace.sendHandoff.testSent", "Test email sent to you"),
        )
      },
    }),
    [previewPayload, t],
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="shrink-0">
        <h3 className="text-sm font-medium text-foreground">
          {t("marketplace.sendHandoff.reviewTitle", "Review production partner email")}
        </h3>
        <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
          {t(
            "marketplace.sendHandoff.reviewHint",
            "This is the email that will be stored on the order and sent to {{vendor}}.",
            { vendor: vendorName },
          )}
        </p>
      </div>

      {previewQuery.isLoading ? (
        <p className="shrink-0 text-xs text-muted-foreground">
          {t("marketplace.sendHandoff.previewLoading", "Rendering production partner email…")}
        </p>
      ) : previewQuery.isError ? (
        <p className="shrink-0 text-xs text-destructive">
          {t("marketplace.sendHandoff.previewFailed", "Couldn't render the production partner email.")}
        </p>
      ) : null}

      <VendorEmailFrame
        from={preview?.from ?? null}
        replyTo={preview?.replyTo ?? null}
        to={recipientsTo}
        cc={recipientsCc}
        bcc={bcc}
        subject={resolvedSubject}
        html={preview?.html ?? null}
        text={preview?.text ?? null}
        view={view}
        onViewChange={setView}
        editable
        disabled={disabled}
        onToChange={onRecipientsToChange}
        onCcChange={onRecipientsCcChange}
        onBccChange={setBcc}
        onSubjectChange={onSubjectChange}
        checks={checks}
        editedCount={editedCount}
        iframeTitle={t("marketplace.sendHandoff.reviewTitle", "Review production partner email")}
        actions={
          <>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7"
              disabled={disabled || previewQuery.isFetching}
              onClick={() => void testSend.mutate()}
            >
              {t("marketplace.sendHandoff.sendTest", "Send test to me")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7"
              disabled={!preview}
              onClick={() => void copy(view === "plain" ? (preview?.text ?? "") : (preview?.html ?? ""))}
            >
              {t("marketplace.sendHandoff.copyBody", "Copy HTML / plain")}
            </Button>
            {onEditSource ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7"
                disabled={disabled}
                onClick={onEditSource}
              >
                {t("marketplace.sendHandoff.editSource", "Edit source")}
              </Button>
            ) : null}
          </>
        }
      />
    </div>
  )
}
